import tableRenderers, { useHtmlTableCellProps } from '@native-html/heuristic-table-plugin'
import React from 'react'
import { GestureResponderEvent, StyleSheet } from 'react-native'
import { CustomRendererProps, MixedStyleRecord } from 'react-native-render-html'
import { openLink } from '../services/browserInApp'
import { QuillStyleClasses } from './quillStyleClasses'

const LINK_COLOR = '#245dc1'

const parseInlineColors = (style?: string) => {
  if (!style) return {}
  const out: { color?: string; backgroundColor?: string } = {}
  const c = /(?:^|;)\s*color\s*:\s*([^;]+)/i.exec(style)
  const b = /background-color\s*:\s*([^;]+)/i.exec(style)
  if (c) out.color = c[1].trim()
  if (b) out.backgroundColor = b[1].trim()
  return out
}

const TAG_STYLE: Record<string, object> = {
  strong: { fontWeight: '700' },
  b: { fontWeight: '700' },
  em: { fontStyle: 'italic' },
  i: { fontStyle: 'italic' },
  u: { textDecorationLine: 'underline' },
  s: { textDecorationLine: 'line-through' },
}

const ColorRenderer = ({ TDefaultRenderer, ...props }: CustomRendererProps<any>) => {
  const tag = props.tnode.tagName as string
  const extra = parseInlineColors(props.tnode.attributes?.style)
  if (props.tnode.markers.anchor) extra.color = LINK_COLOR
  return <TDefaultRenderer {...props} style={{ ...props.style, ...TAG_STYLE[tag], ...extra }} />
}

const CellRenderer = (props: CustomRendererProps<any>) => {
  const cellProps = useHtmlTableCellProps(props)
  const { TDefaultRenderer } = cellProps
  const extra = parseInlineColors(props.tnode.attributes?.style)
  return <TDefaultRenderer {...cellProps} style={{ ...cellProps.style, ...extra }} />
}

export const renderers = {
  span: ColorRenderer,
  strong: ColorRenderer,
  em: ColorRenderer,
  u: ColorRenderer,
  s: ColorRenderer,
  ...tableRenderers,
  td: CellRenderer,
  th: CellRenderer,
}

export const baseStyle = { fontSize: 16, lineHeight: 22 }

export const classesStyles = QuillStyleClasses

export const tagsStyles: MixedStyleRecord = {
  p: { marginTop: 0, marginBottom: 8 },
  h1: { fontSize: 30, fontWeight: '700', marginTop: 0, marginBottom: 8, lineHeight: 36 },
  h2: { fontSize: 26, fontWeight: '700', marginTop: 0, marginBottom: 8, lineHeight: 32 },
  h3: { fontSize: 22, fontWeight: '700', marginTop: 0, marginBottom: 6, lineHeight: 28 },
  h4: { fontSize: 18, fontWeight: '700', marginTop: 0, marginBottom: 6, lineHeight: 24 },
  h5: { fontSize: 16, fontWeight: '700', marginTop: 0, marginBottom: 4, lineHeight: 22 },
  h6: { fontSize: 14, fontWeight: '700', marginTop: 0, marginBottom: 4, lineHeight: 20 },
  ul: { marginTop: 0, marginBottom: 8, paddingLeft: 24 },
  ol: { marginTop: 0, marginBottom: 8, paddingLeft: 24 },
  li: { marginBottom: 2 },
  a: { color: LINK_COLOR, textDecorationLine: 'underline', textDecorationColor: LINK_COLOR },
  td: { borderWidth: StyleSheet.hairlineWidth, borderColor: '#c8c8c8', padding: 6 },
  th: { borderWidth: StyleSheet.hairlineWidth, borderColor: '#c8c8c8', padding: 6 },
}

const ORDERED_TYPES = ['decimal', 'lower-alpha', 'lower-roman'] as const

export const renderersProps = {
  a: {
    onPress: (_event: GestureResponderEvent, href: string) => openLink(href),
  },

  ol: {
    getFallbackListStyleTypeFromNestLevel: (nestLevel: number) => ORDERED_TYPES[nestLevel % ORDERED_TYPES.length],
  },

  table: {
    forceStretch: true,
  },
}

const IMG_MAX_WIDTH_RATIO = 0.4

export const computeEmbeddedMaxWidth = (contentWidth: number, tagName: string) => (tagName === 'img' ? contentWidth * IMG_MAX_WIDTH_RATIO : contentWidth)
